import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { collection, getDocs, limit, orderBy, query, Timestamp, where } from "firebase/firestore";
import Card from "react-bootstrap/esm/Card";
import Form from "react-bootstrap/esm/Form";
import Spinner from "react-bootstrap/esm/Spinner";
import Table from "react-bootstrap/esm/Table";
import { db } from "../services/firebase";
import { Globals } from "../globals";
import { gemeindeSubPath } from "../services/gemeindePaths";
import Preacher from "../DTOs/Preacher";

interface LogEntry {
    id: string;
    createdAt: Date;
    kind: "sermon" | "choir";
    channel: "email" | "whatsapp";
    recipientKey: string;
    serviceId: string;
    attempt: number;
    result: "sent" | "failed";
    error?: string;
}

const LOG_DAYS = 30;
const LOG_LIMIT = 200;

/**
 * Read-only "Versandstatus" table: the reminder delivery attempts of the last
 * 30 days for a Gemeinde, written by the Cloud Functions into
 * gemeinden/{id}/notificationLog (one entry per attempt and channel).
 */
export default function DeliveryLog({ gemeindeId, preachers }: { gemeindeId: string; preachers: Preacher[] }) {
    const { t } = useTranslation();
    const [entries, setEntries] = useState<LogEntry[] | null>(null);
    const [failedOnly, setFailedOnly] = useState(false);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const since = Timestamp.fromMillis(Date.now() - LOG_DAYS * 24 * 60 * 60 * 1000);
                const snap = await getDocs(
                    query(
                        collection(db, ...gemeindeSubPath(gemeindeId, Globals.NOTIFICATION_LOG_SUBCOLLECTION)),
                        where("createdAt", ">=", since),
                        orderBy("createdAt", "desc"),
                        limit(LOG_LIMIT)
                    )
                );
                if (cancelled) return;
                setEntries(
                    snap.docs.map((d) => {
                        const data = d.data();
                        return {
                            id: d.id,
                            createdAt: data.createdAt.toDate(),
                            kind: data.kind,
                            channel: data.channel,
                            recipientKey: data.recipientKey ?? "",
                            serviceId: data.serviceId ?? "",
                            attempt: data.attempt ?? 1,
                            result: data.result,
                            error: data.error,
                        };
                    })
                );
            } catch {
                if (!cancelled) setLoadError(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [gemeindeId]);

    const recipientName = (entry: LogEntry) => {
        if (entry.kind === "choir") return t("deliveryLog.conductor");
        const preacher = preachers.find((p) => p.key === entry.recipientKey);
        return preacher ? `${preacher.name} ${preacher.surname}` : entry.recipientKey || "–";
    };

    // Service ids look like "service_2026-10-18_sunday-morning"; show the date part.
    const serviceDate = (serviceId: string) => {
        const match = /(\d{4})-(\d{2})-(\d{2})/.exec(serviceId);
        return match ? `${match[3]}.${match[2]}.${match[1]}` : serviceId;
    };

    const shown = (entries ?? []).filter((e) => !failedOnly || e.result === "failed");

    return (
        <Card className="mt-4">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <span>{t("deliveryLog.section")}</span>
                    <Form.Check
                        type="switch"
                        id="deliveryLogFailedOnly"
                        label={t("deliveryLog.failedOnly")}
                        checked={failedOnly}
                        onChange={(e) => setFailedOnly(e.target.checked)}
                    />
                </div>
            </Card.Header>
            <Card.Body>
                <p className="text-muted">{t("deliveryLog.description", { days: LOG_DAYS })}</p>
                {loadError ? (
                    <p className="text-danger mb-0">{t("errors.loadingData")}</p>
                ) : entries === null ? (
                    <Spinner animation="border" size="sm" />
                ) : shown.length === 0 ? (
                    <p className="text-muted mb-0">{t("deliveryLog.empty")}</p>
                ) : (
                    <Table responsive size="sm" className="mb-0">
                        <thead>
                            <tr>
                                <th>{t("deliveryLog.time")}</th>
                                <th>{t("deliveryLog.service")}</th>
                                <th>{t("deliveryLog.recipient")}</th>
                                <th>{t("deliveryLog.channel")}</th>
                                <th>{t("deliveryLog.result")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map((entry) => (
                                <tr key={entry.id}>
                                    <td className="text-nowrap">
                                        {entry.createdAt.toLocaleString(t("calendar.locale") as string, {
                                            day: "2-digit",
                                            month: "2-digit",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}
                                    </td>
                                    <td className="text-nowrap">
                                        {serviceDate(entry.serviceId)}
                                        {entry.kind === "choir" ? ` · ${t("deliveryLog.choir")}` : ""}
                                    </td>
                                    <td>{recipientName(entry)}</td>
                                    <td>{entry.channel === "email" ? "E-Mail" : "WhatsApp"}</td>
                                    <td>
                                        <span className={`gl-badge gl-badge--${entry.result === "sent" ? "ok" : "warn"}`}>
                                            {t(entry.result === "sent" ? "deliveryLog.sent" : "deliveryLog.failed")}
                                        </span>
                                        {entry.attempt > 1 && (
                                            <span className="text-muted ms-2">
                                                {t("deliveryLog.attempt", { attempt: entry.attempt })}
                                            </span>
                                        )}
                                        {entry.error && <div className="text-muted small">{entry.error}</div>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    );
}
